import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { EMAIL_SEND_ENDPOINT } from "../utils/config";

interface SendEmailRequest {
  topic: string;
  contextId: number;
  contextState: string | null;
}

export interface QueuedMessageLogEntry {
  id: string;
  topic: string;
  loanId: string;
  contextState: string | null;
  queuedAt: string;
  status: "sent" | "failed";
}

export function useEmailQueueForm() {
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [loanId, setLoanId] = useState("");
  const [contextState, setContextState] = useState("");
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState<QueuedMessageLogEntry[]>([]);
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());

  const openTopic = useCallback((topic: string) => {
    setSelectedTopic(topic);
    setLoanId("");
    setContextState("");
  }, []);

  const closeDialog = useCallback(() => {
    setSelectedTopic(null);
    setLoanId("");
    setContextState("");
  }, []);

  const sendRequest = useCallback(
    async (topic: string, loanIdValue: string, contextStateValue: string | null) => {
      const contextId = Number(loanIdValue);
      const request: SendEmailRequest = { topic, contextId, contextState: contextStateValue };

      try {
        await axios.post(EMAIL_SEND_ENDPOINT, request);
        toast.success(`📨 Queued "${topic}" for Loan ${loanIdValue}`, { position: "bottom-right" });
        setLog((prev) => [
          {
            id: crypto.randomUUID(),
            topic,
            loanId: loanIdValue,
            contextState: contextStateValue,
            queuedAt: new Date().toISOString(),
            status: "sent",
          },
          ...prev,
        ]);
        return true;
      } catch (err) {
        console.error(err);
        toast.error("💥 Failed to queue message", { position: "bottom-right" });
        setLog((prev) => [
          {
            id: crypto.randomUUID(),
            topic,
            loanId: loanIdValue,
            contextState: contextStateValue,
            queuedAt: new Date().toISOString(),
            status: "failed",
          },
          ...prev,
        ]);
        return false;
      }
    },
    []
  );

  const handleSend = useCallback(async () => {
    if (!selectedTopic) return;
    const topic = selectedTopic;
    const trimmedContextState = contextState.trim();
    const contextStateValue = trimmedContextState === "" ? null : trimmedContextState;

    setSending(true);
    const succeeded = await sendRequest(topic, loanId, contextStateValue);
    setSending(false);
    if (succeeded) closeDialog();
  }, [selectedTopic, loanId, contextState, sendRequest, closeDialog]);

  const resend = useCallback(
    async (entry: QueuedMessageLogEntry) => {
      setResendingIds((prev) => new Set(prev).add(entry.id));
      await sendRequest(entry.topic, entry.loanId, entry.contextState);
      setResendingIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
    },
    [sendRequest]
  );

  return {
    selectedTopic,
    loanId,
    setLoanId,
    contextState,
    setContextState,
    sending,
    log,
    resendingIds,
    openTopic,
    closeDialog,
    handleSend,
    resend,
  };
}
